import React from 'react';
import { styled, keyframes } from '@mui/material/styles';
import { Box, Button, Typography, Modal, FormControlLabel, TextField, Switch } from '@mui/material';

// 커스텀 드롭다운 화살표 아이콘
export const DropdownArrowIcon = props => (
  <Box
    {...props}
    sx={{
      position: 'absolute',
      right: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      pointerEvents: 'none',
    }}>
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8" fill="none">
      <path
        d="M7.75135 7.69111C7.33639 8.10296 6.66361 8.10296 6.24865 7.69111L0.31335 1.80032C-0.356033 1.13596 0.118053 0 1.0647 0H12.9353C13.8819 0 14.356 1.13596 13.6866 1.80032L7.75135 7.69111Z"
        fill="#4AB3BC"
      />
    </svg>
  </Box>
);

// ============================================
// Error Animation
// ============================================
const errorPulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4);
  }
  50% {
    box-shadow: 0 0 0 6px rgba(220, 38, 38, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0);
  }
`;

export const ErrorFieldWrapper = styled(Box, {
  shouldForwardProp: prop => prop !== 'hasError',
})(({ hasError }) => ({
  flex: 1,
  borderRadius: '4px',
  '& > *': {
    width: '100%',
  },
  ...(hasError && {
    // 애니메이션
    animation: `${errorPulseGlow} 1.5s ease-in-out infinite`,
    // Outlined Input (TextField, DatePicker 등) - fieldset만 타겟
    '& .MuiOutlinedInput-notchedOutline': {
      border: '2px solid #dc2626 !important',
    },
    // BootstrapInput (FormInputDropdown) - OutlinedInput이 아닌 InputBase만 타겟
    '& .MuiInputBase-root:not(.MuiOutlinedInput-root) .MuiInputBase-input': {
      border: '2px solid #dc2626 !important',
    },
    // Standard/Filled Input
    '& .MuiInput-underline:before, & .MuiInput-underline:after': {
      borderBottom: '2px solid #dc2626 !important',
    },
  }),
}));

// ============================================
// Modal Container & Layout
// ============================================
export const StyledModal = styled(Modal)({});

export const ModalContainer = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '540px',
  maxHeight: '90vh',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
});

export const ModalHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '14px 24px',
  borderRadius: '8px 8px 0 0',
  backgroundColor: '#262626',
});

export const ModalHeaderTitle = styled(Typography)({
  fontSize: '16px',
  fontWeight: 500,
  color: 'white',
});

export const CloseIconWrapper = styled(Box)({
  width: '24px',
  height: '24px',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  '&:hover': {
    opacity: 0.7,
  },
});

export const ModalContent = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  backgroundColor: '#fafbfc',
  display: 'flex',
  flexDirection: 'column',
  gap: '24px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
    '&:hover': {
      background: '#a1a1a1',
    },
  },
});

export const ModalFooter = styled(Box)({
  padding: '16px 24px',
  backgroundColor: '#fafbfc',
  display: 'flex',
  justifyContent: 'center',
  gap: '16px',
});

// ============================================
// Tab Buttons
// ============================================
export const TabContainer = styled(Box)({
  display: 'flex',
  width: '100%',
});

export const TabButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'position',
})(({ active, position }) => ({
  flex: 1,
  padding: '8px 24px',
  fontSize: '16px',
  fontWeight: 500,
  letterSpacing: '-0.16px',
  lineHeight: 1.35,
  minWidth: 'unset',
  backgroundColor: active ? '#262626' : '#fff',
  color: active ? '#fff' : '#141414',
  border: active ? 'none' : '1px solid #141414',
  borderRadius: position === 'first' ? '8px 0 0 8px' : position === 'last' ? '0 8px 8px 0' : 0,
  // 중간 탭들의 왼쪽 테두리 제거 (중복 방지)
  ...(position !== 'first' &&
    !active && {
      borderLeft: 'none',
    }),
  '&:hover': {
    backgroundColor: active ? '#262626' : '#f5f5f5',
  },
}));

// ============================================
// Content Section (White Card)
// ============================================
export const ContentSection = styled(Box)({
  backgroundColor: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '8px',
  padding: '16px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

// ============================================
// Toggle Switch Row
// ============================================
export const ToggleRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
});

export const ToggleLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
});

export const ToggleWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

export const ClassTypeFilterRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  width: '100%',
});

export const classTypeSelectStyle = {
  minWidth: '120px',
  height: '44px',
  borderRadius: '4px',
  backgroundColor: '#fff',
  fontSize: '14px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#a6a6a6',
  },
  '& .MuiSelect-select': {
    padding: '10px 14px',
    fontSize: '14px',
    fontWeight: 500,
  },
};

export const ToggleText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#737373',
});

export const CustomSwitch = styled(Switch)({
  width: 40,
  height: 20,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 2,
    '&.Mui-checked': {
      transform: 'translateX(20px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#4ab3bc',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: '#E0E0E0',
    opacity: 1,
  },
});

// ============================================
// Form Row & Layout
// ============================================
export const FormFieldGroup = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
});

export const FormFieldGroupHalf = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  flex: 1,
  minWidth: 0,
});

export const FormFieldRow = styled(Box)({
  display: 'flex',
  gap: '16px',
  width: '100%',
});

export const FormFieldLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
});

// 판매금액을 상품 원가와 다르게 고쳤을 때 라벨 옆에 기준 금액을 같이 보여준다.
export const BasePriceHint = styled('span')({
  marginLeft: '6px',
  fontSize: '11px',
  fontWeight: 500,
  color: '#4AB3BC',
});

// 포인트 사용 필드의 "최소 N P 사용" 안내 문구 (라벨 옆 작은 회색 글씨)
export const PointMinUseHint = styled('span')({
  fontSize: '10px',
  fontWeight: 400,
  color: '#9a9a9a',
  marginLeft: '4px',
  position: 'relative',
  top: '1px',
  // 문구가 길어지면 잘라서 한 줄 유지(레이아웃 밀림 방지), 전체는 title(hover)로
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  minWidth: 0,
  flexShrink: 1,
});

// 포인트 최소·최대 안내 캡션 — 입력칸 아래 한 줄. 라벨 옆에 붙이면 문구가 길 때
// 라벨이 줄바꿈되며 좌우 컬럼 높이가 어긋난다(UI 깨짐). 넘치면 말줄임 + title 로 전체 노출.
export const PointHintCaption = styled('span')({
  display: 'block',
  fontSize: '12px',
  fontWeight: 400,
  color: '#9a9a9a',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  marginTop: '2px',
});

export const FormRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '16px',
});

export const FormRowSmall = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  marginBottom: '12px',
});

export const FormRowMemo = styled(Box)({
  display: 'flex',
  marginBottom: '12px',
});

export const FormLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  width: '110px',
  flexShrink: 0,
  color: '#262626',
});

export const FormInputWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '16px',
  position: 'relative',
});

export const FormButtonWrapper = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
});

export const FormButton = styled(Button)({
  flex: 1,
  height: '44px',
  fontSize: '14px',
  fontWeight: '600',
  color: '#262626',
  border: '1px solid #262626',
  borderRadius: '4px',
  padding: '12px 16px',
  '&:hover': {
    background: '#262626',
    color: '#fff',
  },
});

export const LockerExtendButton = styled(Button)({
  width: '80px',
  height: '32px',
  fontSize: '12px',
  fontWeight: '600',
  color: '#fff',
  backgroundColor: '#4AB3BC',
  borderRadius: '4px',
  padding: '12px 16px',
  flexShrink: 0,
  '&:hover': {
    backgroundColor: '#404040',
  },
  '&.Mui-disabled': {
    backgroundColor: '#d9d9d9',
    color: '#999',
  },
});

export const FormInputWrapperFull = styled(Box)({
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
});

export const CurrencyUnit = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  minWidth: '20px',
  flexShrink: 0,
});

export const CountUnit = styled(Typography)({
  fontSize: '13px',
  fontWeight: 500,
  color: '#737373',
  minWidth: '20px',
  flexShrink: 0,
});

export const DateSeparator = styled(Typography)({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  fontSize: '18px',
  fontWeight: 600,
  color: '#737373',
});

// ============================================
// Input with Suffix (원, 회 등)
// ============================================
export const InputWithSuffix = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #a6a6a6',
  borderRadius: '4px',
  padding: '0 16px',
  height: '44px',
  backgroundColor: '#fff',
  gap: '8px',
  '& input': {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#141414',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: '#a6a6a6',
    },
  },
});

export const InputWithSuffixDisabled = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #a6a6a6',
  borderRadius: '4px',
  padding: '0 16px',
  height: '44px',
  backgroundColor: '#f5f5f5',
  gap: '8px',
  '& input': {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#a6a6a6',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: '#a6a6a6',
    },
  },
});

export const OutstandingInput = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  border: '1px solid #a6a6a6',
  borderRadius: '4px',
  padding: '0 16px',
  height: '44px',
  backgroundColor: '#fff',
  gap: '8px',
  '& input': {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    fontWeight: 500,
    color: '#ee735a',
    backgroundColor: 'transparent',
    '&::placeholder': {
      color: '#a6a6a6',
    },
  },
});

// ============================================
// Radio & Checkbox
// ============================================
export const RadioWrapper = styled(Box)({
  flex: 1,
});

export const StyledFormControlLabel = styled(FormControlLabel)({
  marginLeft: 0,
  marginRight: '16px',
  whiteSpace: 'nowrap',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    fontWeight: 400,
    whiteSpace: 'nowrap',
  },
});

// ============================================
// Duet Lesson Member Selection
// ============================================
export const SelectedMembersWrapper = styled(Box)({
  display: 'flex',
  width: '100%',
  gap: '8px',
  justifyContent: 'flex-start',
  marginBottom: '15px',
  paddingLeft: '110px',
});

export const SelectedMemberSpacer = styled(Box)({
  display: 'none',
});

export const MemberChip = styled(Typography)({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: '#262626',
  color: '#fff',
  borderRadius: '20px',
  padding: '6px 12px',
  height: '32px',
  fontSize: '14px',
  gap: '8px',
});

// ============================================
// Split Payment
// ============================================
export const SplitPaymentWrapper = styled(Box)({
  marginBottom: '12px',
  paddingLeft: '110px',
});

export const SplitPaymentRow = styled(Box)({
  display: 'flex',
  alignItems: 'flex-start',
  gap: '16px',
});

export const SplitPaymentColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  gap: '8px',
});

export const SplitPaymentInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

// 회원 컬럼 안에서 '결제금액 / 미수금' 처럼 라벨을 인풋 왼쪽에 두는 행
export const SplitPaymentFieldRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
});

export const SplitPaymentFieldLabel = styled(Typography)({
  fontSize: '13px',
  color: '#737373',
  minWidth: '56px',
  flexShrink: 0,
});

export const SplitPaymentValidation = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '12px',
});

export const ValidationText = styled(Typography)(({ isMatch }) => ({
  fontSize: '12px',
  color: isMatch ? '#0F7F85' : '#DF2323',
  fontWeight: isMatch ? 'normal' : 'bold',
}));

// ============================================
// Buttons
// ============================================
export const OpenButton = styled(Button)({
  background: '#0F7F85',
  borderRadius: '8px',
  marginRight: '20px',
  color: 'white',
  fontSize: '14px',
  fontWeight: 500,
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#0d6d72',
  },
});

export const CancelButton = styled(Button)({
  background: '#D9D9D9',
  color: '#141414',
  borderRadius: '8px',
  padding: '12px 32px',
  height: '40px',
  fontSize: '16px',
  fontWeight: 600,
  minWidth: '92px',
  '&:hover': {
    backgroundColor: '#c9c9c9',
  },
});

export const AddToCartButton = styled(Button)({
  background: '#fff',
  color: '#4ab3bc',
  border: '1px solid #4ab3bc',
  borderRadius: '8px',
  padding: '12px 24px',
  height: '40px',
  fontSize: '16px',
  fontWeight: 600,
  minWidth: '92px',
  '&:hover': {
    backgroundColor: '#f0fafa',
  },
});

export const RefundCancelButton = styled(Button)({
  background: '#fff',
  color: '#E15241',
  border: '1px solid #E15241',
  borderRadius: '8px',
  padding: '12px 24px',
  height: '40px',
  fontSize: '16px',
  fontWeight: 600,
  minWidth: '92px',
  '&:hover': {
    backgroundColor: '#fdf2f0',
    border: '1px solid #E15241',
  },
});

export const SaveButton = styled(Button)(({ disabled }) => ({
  background: '#4ab3bc',
  color: 'white',
  borderRadius: '8px',
  padding: '12px 24px',
  height: '40px',
  fontSize: '16px',
  fontWeight: 600,
  minWidth: '200px',
  opacity: disabled ? 0.6 : 1,
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  '&:hover': {
    backgroundColor: disabled ? '#4ab3bc' : '#3a9da5',
  },
}));

// ============================================
// Form Input Styles (for sx prop replacement)
// ============================================
export const dropdownStyle = {
  flex: 1,
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: '4px',
    backgroundColor: '#fff',
    border: '1px solid #a6a6a6',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiInputBase-input': {
    padding: '12px 16px !important',
    fontSize: '14px',
    fontWeight: 500,
    color: '#141414',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiSelect-icon': {
    color: '#141414',
    right: '16px',
  },
};

export const textInputStyle = {
  flex: 1,
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: '4px',
    border: '1px solid #a6a6a6',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#141414',
    '&::placeholder': {
      color: '#a6a6a6',
    },
  },
};

// textInputStyle 과 동일한 박스 구조(자체 border + notchedOutline 제거)로 맞춰 편집 가능한 인풋과 크기를 통일한다.
export const disabledInputStyle = {
  flex: 1,
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#F2F2F2',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '14px',
    color: '#A6A6A6',
  },
};

export const dateInputStyle = {
  width: '216px',
  minWidth: '216px',
  background: '#fff',
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: '4px',
    border: '1px solid #a6a6a6',
    paddingRight: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#141414',
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },
  '& .MuiIconButton-root': {
    padding: 0,
    marginRight: 0,
  },
};

export const dateInputStyleFlex = {
  flex: 1,
  background: '#fff',
  '& .MuiInputBase-root': {
    height: '44px',
    borderRadius: '4px',
    border: '1px solid #a6a6a6',
    paddingRight: '14px',
  },
  '& .MuiOutlinedInput-notchedOutline': {
    border: 'none',
  },
  '& .MuiOutlinedInput-input': {
    padding: '12px 16px',
    fontSize: '14px',
    fontWeight: 500,
    color: '#141414',
  },
  '& .MuiInputAdornment-root': {
    marginLeft: 0,
  },
  '& .MuiIconButton-root': {
    padding: 0,
    marginRight: 0,
  },
};

export const autocompleteStyle = {
  flex: 1,
  '& .MuiAutocomplete-inputRoot': {
    height: '44px',
    padding: '0 !important',
    borderRadius: '4px',
  },
};

export const autocompleteInputStyle = {
  '& .MuiOutlinedInput-root': {
    height: '44px',
    padding: 0,
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '0 14px',
    fontSize: '14px',
  },
};

export const splitPaymentInputStyle = {
  flex: 1,
  '& .MuiInputBase-root': {
    height: '40px',
    borderRadius: '4px',
  },
  '& .MuiOutlinedInput-input': {
    padding: '8px 12px',
    fontSize: '14px',
  },
};

// 나눠결제 미수금 인풋 — 단건 미수금 필드와 같은 강조색
export const splitOutstandingInputStyle = {
  ...splitPaymentInputStyle,
  '& .MuiOutlinedInput-input': {
    ...splitPaymentInputStyle['& .MuiOutlinedInput-input'],
    color: '#ee735a',
  },
};

export const memoTextAreaStyle = {
  width: '100%',
  height: '88px',
  padding: '14px 16px',
  fontSize: '14px',
  fontWeight: 500,
  border: '1px solid #a6a6a6',
  borderRadius: '4px',
  color: '#141414',
  resize: 'none',
  fontFamily: 'inherit',
  '&::placeholder': {
    color: '#a6a6a6',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#4ab3bc',
  },
};

// ============================================
// Dropdown Option with Status Badge
// ============================================
export const DropdownOptionWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
});

export const DropdownOptionLabel = styled(Typography)({
  fontSize: '14px',
  color: '#262626',
});

export const DropdownStatusBadge = styled(Typography)(({ status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'ON_SALE':
      case 'APPROVED':
        return '#0F7F85'; // 판매중 - teal
      case 'SALE_END':
      case 'EXPIRED':
        return '#EE735A'; // 판매종료 - orange/red
      default:
        return '#0F7F85';
    }
  };

  return {
    fontSize: '13px',
    fontWeight: 500,
    color: getStatusColor(),
  };
});

// ============================================
// Receipt Type Tabs (현금영수증 발행구분)
// ============================================
export const ReceiptTypeTabContainer = styled(Box)({
  display: 'flex',
  width: '100%',
  height: '36px',
  borderRadius: '999px',
  border: '1px solid #D9D9D9',
  overflow: 'hidden',
  backgroundColor: '#fff',
});

export const ReceiptTypeTab = styled(Box, {
  shouldForwardProp: prop => prop !== 'active' && prop !== 'position',
})(({ active, position }) => ({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  // 반폭 칸(약 190px)에 "개인소득공제 | 사업자지출증빙" 두 탭이 들어가야 한다. 14px 이면 오른쪽 글자가 잘린다.
  fontSize: '12px',
  fontWeight: 500,
  whiteSpace: 'nowrap',
  padding: '0 6px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  backgroundColor: active ? '#F3F9FA' : '#fff',
  color: active ? '#4AB3BC' : '#737373',
  borderRadius: position === 'left' ? '999px 0 0 999px' : position === 'right' ? '0 999px 999px 0' : 0,
  '&:hover': {
    backgroundColor: active ? '#F3F9FA' : '#f5f5f5',
  },
}));

// ============================================
// Cart Section (담기섹션)
// ============================================
export const ModalContainerWithCart = styled(Box)({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  display: 'flex',
  maxHeight: '90vh',
  backgroundColor: 'transparent',
  borderRadius: '8px',
});

export const ModalMainSection = styled(Box)({
  width: '540px',
  backgroundColor: '#fff',
  boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.15)',
  borderRadius: '8px 0 0 8px',
  display: 'flex',
  flexDirection: 'column',
  maxHeight: '90vh',
});

export const CartSection = styled(Box)({
  width: '440px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#F3F9FA',
  borderRadius: '0 8px 8px 0',
  overflow: 'hidden',
});

export const CartItemsContainer = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  padding: '24px',
  borderTop: '1px solid #d9d9d9',
  borderRight: '1px solid #d9d9d9',
  borderTopRightRadius: '8px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '3px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#c1c1c1',
    borderRadius: '3px',
  },
});

export const CartItemCard = styled(Box)({
  backgroundColor: '#fff',
  border: '1px solid #d9d9d9',
  borderRadius: '12px',
  padding: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
});

export const CartItemHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
});

export const CartItemInfo = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
  flex: 1,
  minWidth: 0,
  marginRight: '8px',
});

export const CartItemTypeLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4ab3bc',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
});

export const CartItemName = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#141414',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
});

export const CartItemSubLabel = styled(Typography)({
  fontSize: '12px',
  fontWeight: 500,
  color: '#737373',
  letterSpacing: '-0.12px',
  lineHeight: 1.35,
});

export const CartItemRow = styled(Box)({
  display: 'flex',
  gap: '8px',
  alignItems: 'center',
});

export const CartItemLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
});

export const CartItemValue = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#141414',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
});

export const CartItemValueHighlight = styled(Typography)({
  fontSize: '16px',
  fontWeight: 600,
  color: '#4ab3bc',
  letterSpacing: '-0.16px',
  lineHeight: 1.35,
});

export const CartDeleteButton = styled(Button)({
  // 담당자 칩이 여러 줄로 늘어나도 버튼이 '삭/제' 로 꺾이지 않게
  flexShrink: 0,
  whiteSpace: 'nowrap',
  padding: '6px 12px',
  border: '1px solid #737373',
  borderRadius: '4px',
  color: '#737373',
  fontSize: '14px',
  fontWeight: 500,
  minWidth: 'unset',
  height: 'auto',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#404040',
    color: '#404040',
  },
});

export const CartItemPriceInfo = styled(Box)({
  backgroundColor: '#fafbfc',
  borderRadius: '8px',
  padding: '12px 16px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '20px',
});

export const CartPriceColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  gap: '4px',
  width: '172px',
});

export const CartPriceRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
});

export const CartPriceLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
});

export const CartPriceValue = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#737373',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
  textAlign: 'center',
});

export const CartPriceValueOutstanding = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#ee735a',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
  textAlign: 'center',
});

export const CartPriceValueFinal = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4ab3bc',
  letterSpacing: '-0.14px',
  lineHeight: 1.35,
  textAlign: 'right',
});

export const CartPriceDivider = styled(Box)({
  width: '1px',
  alignSelf: 'stretch',
  backgroundColor: '#d9d9d9',
});

// ============================================
// Cart Summary Table
// ============================================
export const CartSummaryTable = styled(Box)({
  display: 'flex',
  width: '100%',
});

export const CartSummaryLabelColumn = styled(Box)({
  width: '140px',
  backgroundColor: '#fafbfc',
  display: 'flex',
  flexDirection: 'column',
});

export const CartSummaryValueColumn = styled(Box)({
  flex: 1,
  backgroundColor: '#fff',
  display: 'flex',
  flexDirection: 'column',
});

export const CartSummaryCell = styled(Box, {
  shouldForwardProp: prop => !['isFirst', 'isLast', 'isLabel'].includes(prop),
})(({ isFirst, isLast, isLabel }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isLabel ? 'center' : 'flex-end',
  padding: '12px 16px',
  height: '41px',
  borderBottom: isLast ? 'none' : '1px solid #d9d9d9',
  borderLeft: !isLabel ? 'none' : '1px solid #d9d9d9',
  borderRight: '1px solid #d9d9d9',
  borderTop: isFirst ? '1px solid #d9d9d9' : 'none',
  ...(isLast && !isLabel && {
    borderBottomRightRadius: '8px',
  }),
}));

export const CartSummaryLabel = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#737373',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const CartSummaryLabelOutstanding = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#ee735a',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const CartSummaryLabelTotal = styled(Typography)({
  fontSize: '14px',
  fontWeight: 600,
  color: '#4ab3bc',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const CartSummaryValue = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#404040',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const CartSummaryValueLarge = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#737373',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const CartSummaryValueOutstanding = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#ee735a',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

export const CartSummaryValueTotal = styled(Typography)({
  fontSize: '18px',
  fontWeight: 600,
  color: '#4ab3bc',
  textAlign: 'center',
  whiteSpace: 'nowrap',
});

// ============================================
// Empty Cart State
// ============================================
export const EmptyCartContainer = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '48px 24px',
  color: '#a6a6a6',
});

export const EmptyCartText = styled(Typography)({
  fontSize: '14px',
  fontWeight: 500,
  color: '#a6a6a6',
  textAlign: 'center',
});
